import { Edit, Trash, XCircle } from 'react-feather';
import { Route, Switch, useHistory } from 'react-router-dom';
import { isPast } from 'date-fns';
import { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import CancelScheduledMessageMutation from './CancelScheduledMessageMutation';
import DeleteMessageMutation from './DeleteMessageMutation';
import EditMessageAudienceForm from './EditMessageAudienceForm';
import EditMessageContentForm from './EditMessageContentForm';
import EditMessageTitleForm from './EditMessageTitleForm';
import GetMessageQuery from './GetMessageQuery';
import GetMessageRecipientsQuery from './GetMessageRecipientsQuery';
import MessageStatusLabel from './MessageStatusLabel';
import OptionsDropdown, { Option } from '../../../common/Common/OptionsDropdown';
import PreviewMessageForm from './PreviewMessageForm';
import SendMessagePage from './SendMessagePage';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export interface MessagePageProps {
  messageId: string;
  route?: string;
}

const MessagePage = ({ messageId, route }: MessagePageProps) => {
  const { t } = useTranslation();
  const { formatInteger, parseDate } = useLocale();
  const history = useHistory();
  const notifier = useNotifier();
  const project = useProject();
  const device = useMediaDevice();

  const { data, refetch, loading } = useQuery(GetMessageQuery, {
    variables: {
      project_id: project.id,
      message_id: messageId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const message = data?.project.message;

  const [loadingRecipients, setLoadingRecipients] = useState(true);

  const {
    data: recipientsData, refetch: refetchRecipients,
  } = useQuery(GetMessageRecipientsQuery, {
    variables: {
      project_id: project.id,
      message_id: messageId,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      setLoadingRecipients(false);
    },
  });

  const recipients = recipientsData?.project.message;

  useDocumentTitle(message?.title || t('messages'));

  useEffect(() => {
    if (message) {
      // Refetch every 3 seconds. The timeout is cleared when the useEffect is being cleaned up.
      // This happens when remaining count of the message changes.
      const timer = window.setInterval(() => {
        const scheduledAt = message.scheduled_at
          ? parseDate(message.scheduled_at, { timezone: 'UTC' })
          : undefined;

        if (message.remaining_count > 0 || (scheduledAt && isPast(scheduledAt))) {
          refetchRecipients();
        }
      }, 3000);

      return () => window.clearInterval(timer);
    }

    return undefined;
  }, [message, parseDate, refetchRecipients]);

  // When the route changes, refetch the message, to make sure the user gets fresh data.
  useEffect(() => {
    refetch();

    if (route === 'send') {
      setLoadingRecipients(true);
      refetchRecipients();
    }
  }, [route, refetch, refetchRecipients]);

  const [deleteMessage] = useMutation(
    DeleteMessageMutation, {
      variables: {
        id: message?.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.message_deleted'));

        history.push(`/${project.id}/communication/messages`);
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: message?.title }),
    body: t('delete_message_description'),
    onConfirm: () => deleteMessage(),
  }, [message]);

  const [cancelSchedule] = useMutation(CancelScheduledMessageMutation, {
    variables: { id: message?.id },
    onCompleted: () => notifier.success(t('notifications.message_saved')),
  });

  const [openEditMessageTitleModal, closeEditMessageTitleModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeEditMessageTitleModal}>
      <EditMessageTitleForm
        message={message}
        onSuccess={() => closeEditMessageTitleModal()}
      />
    </UI.Modal>
  ), [message]);

  if (!message) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <UI.Hero>
        <UI.Section sc={{ compact: true }}>
          <UI.GridContainer sc={{ columns: ['1fr', '3fr 2fr'] }}>
            <UI.Div style={{ minWidth: 0 }}>
              <UI.H1>
                <UI.Truncate>
                  {message.title}
                </UI.Truncate>
              </UI.H1>
              <UI.Link to={`/${project.id}/events/${message.event.id}`} sc={{ secondary: true }}>
                {message.event.title}
              </UI.Link>
            </UI.Div>
            <UI.Div
              sc={{
                flex: device.isSmall && { alignItems: 'center' },
                textAlign: ['left', 'right'],
                mt: [0, 1],
              }}
            >
              <MessageStatusLabel message={message} />

              <OptionsDropdown sc={{ ml: 1 }}>
                {message.scheduled_at && (
                  <Option
                    icon={<XCircle />}
                    label={t('cancel_schedule')}
                    onClick={() => cancelSchedule()}
                  />
                )}
                <Option
                  icon={<Edit />}
                  label={t('edit_title')}
                  onClick={() => openEditMessageTitleModal()}
                  disabled={!message.deletable}
                />
                <Option
                  icon={<Trash />}
                  label={t('delete')}
                  onClick={() => confirmDelete()}
                  disabled={!message.deletable}
                />
              </OptionsDropdown>
            </UI.Div>
          </UI.GridContainer>
        </UI.Section>

        <UI.Container sc={{ compact: true, px: [0, 4] }}>
          <UI.Tabs sc={{ px: [2, 0] }}>
            <UI.Tab to={`/${project.id}/communication/messages/${message.id}`} exact>
              {t('audience')}
              {' '}
              <UI.Small>
                {!recipients && (
                  <UI.Span sc={{ inlineBlock: true }} style={{ width: 20, height: 20, padding: 2 }}>
                    <UI.Loader sc={{ brand: 'gray.300' }} />
                  </UI.Span>
                )}
                {recipients && (
                  <UI.Badge sc={{ loading: loadingRecipients }}>
                    {formatInteger(recipients.recipient_count)}
                  </UI.Badge>
                )}
              </UI.Small>
            </UI.Tab>
            <UI.Tab to={`/${project.id}/communication/messages/${message.id}/content`}>
              {t('content')}
              {' '}
              {message.draft && <UI.RequiredMark />}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/communication/messages/${message.id}/preview`}>
              {t('preview')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/communication/messages/${message.id}/send`}>
              {t('send')}
            </UI.Tab>
          </UI.Tabs>
        </UI.Container>
      </UI.Hero>

      <UI.Section id="MessageTab" sc={{ compact: true }}>
        {loading && (
          <UI.PageLoader />
        )}
        {!loading && (
          <Switch>
            <Route path="/:projectId/communication/messages/:messageId/preview">
              <PreviewMessageForm message={message} />
            </Route>
            <Route path="/:projectId/communication/messages/:messageId/send">
              <SendMessagePage message={message} recipients={loadingRecipients ? undefined : recipients} />
            </Route>
            <Route path="/:projectId/communication/messages/:messageId/content">
              <EditMessageContentForm message={message} />
            </Route>
            <Route path="/:projectId/communication/messages/:messageId">
              <EditMessageAudienceForm message={message} />
            </Route>
          </Switch>
        )}
      </UI.Section>
    </>
  );
};

export default MessagePage;
